import Vue from "vue";
import {
    FetchState,
    DrivewealthAccountPerformanceRequestArgs,
    DrivewealthAccountPerformance,
    DrivewealthAccountPerformanceState,
    DrivewealthAccountIdentifierArgs,
    DrivewealthAccountPerformanceTimePeriod,
    DrivewealthAccountRateOfReturnModel,
    InvestmentBalance
} from "@/shared/types";
import * as storeHelper from "@/shared/storeHelper";
import { defineStore } from "pinia";
import { useDrivewealthStore } from "@/shared/store/drivewealth";
import _ from "lodash";
import { templatedFetchKeyCollectionStore } from "@/shared/store/fetchKeyedCollectionPlugin";
import * as drivewealthService from "@/shared/api/drivewealthService";
import moment from "moment/moment";

const dateFormatString = "YYYY-MM-DD";

export const initialState: DrivewealthAccountPerformanceState = {
    selectedTimePeriod: DrivewealthAccountPerformanceTimePeriod.YearToDate,
    selectedAccountId: 0
};

const useAccountPerformanceDataSubStore = defineStore("drivewealth_accountPerformanceData", () => {
    return templatedFetchKeyCollectionStore<DrivewealthAccountPerformance, DrivewealthAccountPerformance>(
        drivewealthService.getAccountPerformanceData,
    );
});

const useInvestmentBalanceGraphDataSubStore = defineStore("investmentBalanceGraphData", () => {
    return templatedFetchKeyCollectionStore<InvestmentBalance[], InvestmentBalance[]>(
        drivewealthService.getInvestmentBalanceGraphData,
    );
});

const useRateOfReturnSubStore = defineStore("drivewealth_accountPerformance_rateOfReturn", () => {
    return templatedFetchKeyCollectionStore<DrivewealthAccountRateOfReturnModel, DrivewealthAccountRateOfReturnModel>(
        drivewealthService.getAccountPerformanceRateOfReturn,
    );
});

const accountPerformanceItemKeyFormatter = (args: DrivewealthAccountPerformanceRequestArgs): string => {
    return `${args.accountId}_${args.timePeriod}`;
};

export const useDrivewealthAccountPerformanceStore = defineStore("drivewealthAccountPerformance", {
    persist: {
        storage: localStorage,
        paths: storeHelper.getStoragePaths("drivewealthAccountPerformance"),
    },
    state: () => (_.cloneDeep(initialState)),
    getters: {
        accountPerformanceDataStore: () => useAccountPerformanceDataSubStore(),
        rateOfReturnSubStore: () => useRateOfReturnSubStore(),
        investmentBalanceGraphDataSubStore: () => useInvestmentBalanceGraphDataSubStore(),
        accountPerformanceRequestModel (): (args: DrivewealthAccountIdentifierArgs) => DrivewealthAccountPerformanceRequestArgs {
            return (args: DrivewealthAccountIdentifierArgs) => {
                const requestModel: DrivewealthAccountPerformanceRequestArgs = {
                    ...args,
                    timePeriod: this.selectedTimePeriod,
                };

                return requestModel;
            };
        },
        accountPerformanceLoaded (): (args: DrivewealthAccountIdentifierArgs) => boolean {
            return (args: DrivewealthAccountIdentifierArgs) => {
                const requestModel = this.accountPerformanceRequestModel(args);
                const itemKey = accountPerformanceItemKeyFormatter(requestModel);
                const performanceLookup = this.accountPerformanceDataStore.fetchStates[itemKey];

                if (performanceLookup) {
                    return performanceLookup === FetchState.Loaded;
                }

                return false;
            };
        },
        accountPerformanceLoading (): (args: DrivewealthAccountIdentifierArgs) => boolean {
            return (args: DrivewealthAccountIdentifierArgs) => {
                const requestModel = this.accountPerformanceRequestModel(args);
                const itemKey = accountPerformanceItemKeyFormatter(requestModel);
                const performanceLookup = this.accountPerformanceDataStore.fetchStates[itemKey];

                if (performanceLookup) {
                    return performanceLookup === FetchState.Loading;
                }

                return false;
            };
        },
        accountPerformanceResult (): (args: DrivewealthAccountIdentifierArgs) => DrivewealthAccountPerformance {
            return (args: DrivewealthAccountIdentifierArgs) => {
                const requestModel = this.accountPerformanceRequestModel(args);
                const itemKey = accountPerformanceItemKeyFormatter(requestModel);
                const performanceLookup = this.accountPerformanceDataStore.items[itemKey];

                if (performanceLookup) {
                    return performanceLookup;
                }

                return null;
            };
        },
        rateOfReturnLoaded (): (accountId: number) => boolean {
            return (accountId: number) => {
                const rateOfReturnLookup = this.rateOfReturnSubStore.fetchStates[accountId];

                if (rateOfReturnLookup) {
                    return rateOfReturnLookup === FetchState.Loaded;
                }

                return false;
            };
        },
        rateOfReturnResult (): (accountId: number) => DrivewealthAccountRateOfReturnModel {
            return (accountId: number) => {
                const rateOfReturnLookup = this.rateOfReturnSubStore.items[accountId];

                if (rateOfReturnLookup) {
                    return rateOfReturnLookup;
                }

                return null;
            };
        },
        investmentBalanceGraphDataLoaded (): (args: DrivewealthAccountIdentifierArgs) => boolean {
            return (args: DrivewealthAccountIdentifierArgs) => {
                const requestModel = this.accountPerformanceRequestModel(args);
                const itemKey = accountPerformanceItemKeyFormatter(requestModel);
                const graphDataLookup = this.investmentBalanceGraphDataSubStore.fetchStates[itemKey];

                if (graphDataLookup) {
                    return graphDataLookup === FetchState.Loaded;
                }

                return false;
            };
        },
        investmentBalanceGraphDataLoading (): (args: DrivewealthAccountIdentifierArgs) => boolean {
            return (args: DrivewealthAccountIdentifierArgs) => {
                const requestModel = this.accountPerformanceRequestModel(args);
                const itemKey = accountPerformanceItemKeyFormatter(requestModel);
                const graphDataLookup = this.investmentBalanceGraphDataSubStore.fetchStates[itemKey];

                if (graphDataLookup) {
                    return graphDataLookup === FetchState.Loading;
                }

                return false;
            };
        },
        investmentBalanceGraphData (): (args: DrivewealthAccountIdentifierArgs) => InvestmentBalance[] {
            return (args: DrivewealthAccountIdentifierArgs) => {
                const requestModel = this.accountPerformanceRequestModel(args);
                const itemKey = accountPerformanceItemKeyFormatter(requestModel);
                const graphDataLookup = this.investmentBalanceGraphDataSubStore.items[itemKey];

                if (graphDataLookup) {
                    const drivewealthStore = useDrivewealthStore();
                    const accountSummary = drivewealthStore.accountSummaryById(args.accountId);
                    return [
                        ...graphDataLookup,
                        { date: moment().format("YYYY-MM-DD"), balance: accountSummary.displayBalance }
                    ];
                }

                return null;
            };
        },
    },
    actions: {
        setSelectedTimePeriod (timePeriod: DrivewealthAccountPerformanceTimePeriod) {
            this.selectedTimePeriod = timePeriod;
        },
        setSelectedAccountId (accountId: number) {
            this.selectedAccountId = accountId;
        },
        async getAccountPerformance (
            args: DrivewealthAccountIdentifierArgs
        ): Promise<any> {
            const requestModel = this.accountPerformanceRequestModel(args);
            const itemKey = accountPerformanceItemKeyFormatter(requestModel);
            return useAccountPerformanceDataSubStore().fetchKeyedApiCall(
                { username: requestModel.username, partnerId: requestModel.partnerId },
                itemKey,
                requestModel
            )
                .catch(err => {
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        async getRateOfReturn (
            args: DrivewealthAccountIdentifierArgs
        ): Promise<any> {
            const requestModel = this.accountPerformanceRequestModel(args);
            return useRateOfReturnSubStore().fetchKeyedApiCall(
                { username: requestModel.username, partnerId: requestModel.partnerId },
                `${args.accountId}`,
                { accountId: args.accountId }
            )
                .catch(err => {
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        async getInvestmentBalanceGraphData (
            args: DrivewealthAccountIdentifierArgs
        ): Promise<any> {
            const requestModel = this.accountPerformanceRequestModel(args);
            const itemKey = accountPerformanceItemKeyFormatter(requestModel);
            return useInvestmentBalanceGraphDataSubStore().fetchKeyedApiCall(
                { username: requestModel.username, partnerId: requestModel.partnerId },
                itemKey,
                requestModel
            )
                .catch(err => {
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
    }
});
